

import api from "@/services/api"
import { Component, Vue, Watch, Prop, Mixins, Ref } from "vue-property-decorator"
import { BModal } from "bootstrap-vue"
import { Update } from "@/types/update"
import { Getter, Mutation, Action, State } from "vuex-class"
import VuePerfectScrollBar from "vue-perfect-scrollbar"
import { Team, User } from "@/types"
import DataForm from "@/components/Config/DataForm.vue"
import { CurrentUser } from "@/types/user"
import TextAreaToggleExpand from "../Fields/TextAreaToggleExpand.vue"
import FieldControl from "../Fields/FieldControl.vue"
import VueSelect from "vue-select"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"b-modal": BModal,
		"scrollbar": VuePerfectScrollBar,
		"data-form": DataForm,
		"text-area-expand": TextAreaToggleExpand,
		"field-control": FieldControl,
		"v-select": VueSelect
	}
})

export default class UpdatesEditor extends Vue {
	@Prop({ default: false })
	public show!: boolean;

	@Prop({ default: null })
	public editingUpdate!: Update;

	@Prop({ type: Boolean, default: false })
	public fillVertically!: boolean;

	@Getter("getMe")
	public me!: CurrentUser | null;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getTeams")
	public teams!: Team[];

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	private readonly MIN_TITLE_LENGTH = 3
	private readonly MIN_BODY_LENGTH = 5

	// the update being created / modified
	public modifying: Update | null = null

	private showModal: boolean = false

	// v-model with form components
	public selectedTitle: string | null = null
	public selectedBody: string | null = null
	public selectedTeams: Team[] | null = null

	// validation related
	private hasFailedSubmit: boolean = false
	private titleValidation: boolean = false
	private bodyValidation: boolean = false

	// confirmation modal related
	private deletingUpdate: boolean = false

	@Watch("show")
	public showChanged (shown: boolean) {
		if (shown) {
			this.assignUpdate()
			this.showModal = true
		}
	}

	public mounted () {
		this.assignUpdate()
	}

	public get isModifyingExisting (): boolean {
		return !!this.editingUpdate
	}

	/**
	 * Assigns an Update to 'modifying' as contextually appropriate.
	 * Clones existing update if modifying or otherwise creates a new update.
	 */
	public assignUpdate () {
		this.modifying = this.isModifyingExisting ? Object.assign({}, this.editingUpdate) : {
			id: "",
			tenantId: "",
			created: new Date(Date.now()),
			lastUpdated: new Date(Date.now()),
			createdBy: this.me,
			title: "",
			body: "",
			teams: []
		} as Update

		this.selectedTitle = this.modifying.title
		this.selectedBody = this.modifying.body
		this.selectedTeams = this.currentUpdateTeamList
	}

	/**
	 * Gets the update currently being created or modified.
	 * Creates a new Update object if appropriate.
	 */
	public get getUpdate (): Update {
		if (!this.modifying) {
			this.assignUpdate()
		}

		return this.modifying!
	}

	public get getTitleDisplayString () {
		return this.isModifyingExisting ? "Modify Update" : "Create Update"
	}

	public get getOkayString () {
		return this.isModifyingExisting ? "Save Changes" : "Create"
	}

	public createModalClosed (modified: boolean) {
		this.emitVisibility(false, modified)
		this.hasFailedSubmit = false
		this.titleValidation = false
		this.bodyValidation = false
	}

	public emitVisibility (visible: boolean, modified: boolean = false) {
		this.$emit("visibility-changed", visible, modified)
	}

	public async submitUpdate (submitEvent: any) {
		let submitted: boolean = false

		if (this.formValid) {
			this.modifying!.title = this.selectedTitle!
			this.modifying!.body = this.selectedBody!.trim()
			this.modifying!.teams =
				this.selectedTeams ? this.selectedTeams.map(team => team.id)! : []
			if (!this.modifying!.createdBy) {
				this.modifying!.createdBy = this.me!
			}
			if (this.isModifyingExisting) {
				await api.updatesUpdate(this.modifying!)
			} else {
				await api.updatesCreate(this.modifying!)
			}
			submitted = true
		} else {
			submitEvent.preventDefault()
		}

		this.hasFailedSubmit = !submitted

		if (this.hasFailedSubmit) {
			this.titleValidation = true
			this.bodyValidation = true
		}

		if (submitted) {
			this.createModalClosed(submitted)
		}
	}

	public get currentUpdateTeamList () {
		if (!this.modifying || !this.modifying.teams) {
			return null
		}

		let result: Team[] = []

		this.modifying.teams.forEach((teamId: string) => result.push(this.teamsById[teamId]))

		return result
	}

	public get titleValid (): boolean {
		return (!!this.selectedTitle && this.selectedTitle.length >= this.MIN_TITLE_LENGTH)
	}

	public get bodyValid (): boolean {
		if (!this.selectedBody) {
			return false
		}

		let strippedHtml = this.selectedBody.replace(/<[^>]*>?/gm, "")
		return strippedHtml.trim().length >= this.MIN_BODY_LENGTH
	}

	public get formValid (): boolean {
		return (this.titleValid && this.bodyValid)
	}

	public get changesMadeToExistingUpdate (): boolean {
		return this.isModifyingExisting && (
			(this.selectedTitle !== this.editingUpdate.title) ||
			(this.selectedBody !== this.editingUpdate.body) ||
			this.changesMadeToTeams
		)
	}

	private get changesMadeToTeams (): boolean {
		if (!this.selectedTeams && !this.editingUpdate.teams) {
			return false
		} else if (this.selectedTeams && this.editingUpdate.teams) {
			return this.selectedTeams!.length !== this.editingUpdate.teams.length ||
				this.selectedTeams!.some(team => !this.editingUpdate.teams.includes(team.id))
		} else {
			return true
		}
	}

	public get disableOkayButton (): boolean {
		return !this.formValid || (this.isModifyingExisting && !this.changesMadeToExistingUpdate)
	}

	public get shouldShowBodyInvalid (): boolean {
		return (this.bodyValidation && !this.bodyValid)
	}

	public deleteUpdate () {
		if (this.editingUpdate) {
			this.$emit("delete-update", this.editingUpdate)
			this.showModal = false
			this.createModalClosed(false)
		}
	}

	public cancelDeleteUpdate () {
		this.deletingUpdate = false
	}

	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}
}

